import {MeterItem} from 'primeng/metergroup';
import {ECpmCTaskStatusApiEnum} from '../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-task.api-enums';
import {CpmCServiceRequestApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-service-request.api-model';
import {CpmCServiceRequestTaskStatusCacheApiSchema} from '../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-service-request.api-schemas';
import {StringUtils} from '../../../sdk/src/utils/string.util';
import {TaskUtils} from './task.utils';
import {TableTagDef} from '../../../sdk/src/components/generic-table/table-col-def';
import {TreeTagDef} from '../../../sdk/src/components/generic-tree/tree-col-def';

/**
 * Service request utilities
 */
export abstract class ServiceRequestUtils {
    public static readonly statusDescriptions: Record<string, string> = TaskUtils.statusDescriptions
        ? Object.fromEntries(
              Object.entries(TaskUtils.statusDescriptions).map(([key, value]) => [
                  key,
                  value.replace(/Task/gi, 'A task')
              ])
          )
        : {};
    public static readonly statusTags: (TableTagDef | TreeTagDef)[] = TaskUtils.statusTags
        ? TaskUtils.statusTags.map((x) => ({...x, tooltip: x.tooltip?.replace(/Task/gi, 'A task')}))
        : [];
    public static readonly waitingForInputTag: TableTagDef | TreeTagDef = {
        ...TaskUtils.waitingForInputTag,
        tooltip: TaskUtils.waitingForInputTag.tooltip?.replace(/Task/gi, 'A task')
    };
    public static readonly statusTagsByStatus: Record<ECpmCTaskStatusApiEnum, TableTagDef | TreeTagDef> =
        Object.fromEntries(ServiceRequestUtils.statusTags.map((x) => [x.value, x])) as Record<
            ECpmCTaskStatusApiEnum,
            TableTagDef | TreeTagDef
        >;

    // ===============================================================
    // ===============================================================

    /**
     * Get the status of a service request.
     * @param serviceRequest The service request to evaluate.
     * @returns An object containing the status and whether it is waiting for user input.
     */
    public static getStatus(serviceRequest: CpmCServiceRequestApiModel): {
        status: ECpmCTaskStatusApiEnum;
        waitingForInput: boolean;
    } {
        const statusCache = ServiceRequestUtils.ensureStatusCache(serviceRequest);
        let isWaitingForInput = statusCache.waiting_user_input > 0;
        let status: ECpmCTaskStatusApiEnum = ECpmCTaskStatusApiEnum.IN_PROGRESS;
        if (statusCache.failed != 0) {
            status = ECpmCTaskStatusApiEnum.FAILED;
            isWaitingForInput = false;
        } else if (statusCache.success === statusCache.total) {
            status = ECpmCTaskStatusApiEnum.SUCCESS;
        } else if (statusCache.success == 0 && statusCache.in_progress == 0) {
            status = ECpmCTaskStatusApiEnum.READY;
        } else {
            status = ECpmCTaskStatusApiEnum.IN_PROGRESS;
        }

        return {status, waitingForInput: isWaitingForInput};
    }
    /**
     * Get the progress of a service request.
     * @param serviceRequest The service request to evaluate.
     * @returns An object containing the progress details, tooltip, and completion percentage.
     */
    public static getProgress(serviceRequest: CpmCServiceRequestApiModel): {
        details: MeterItem[];
        tooltip: string;
        percentage: number;
    } {
        const order = [
            ECpmCTaskStatusApiEnum.SUCCESS,
            ECpmCTaskStatusApiEnum.FAILED,
            ECpmCTaskStatusApiEnum.IN_PROGRESS,
            ECpmCTaskStatusApiEnum.READY,
            ECpmCTaskStatusApiEnum.CREATED
        ];

        const statusCache = ServiceRequestUtils.ensureStatusCache(serviceRequest);
        const countPerStatus = Object.fromEntries(Object.keys(ECpmCTaskStatusApiEnum).map((x) => [x, 0]));

        countPerStatus[ECpmCTaskStatusApiEnum.CREATED] = statusCache.created;
        countPerStatus[ECpmCTaskStatusApiEnum.READY] = statusCache.ready;
        countPerStatus[ECpmCTaskStatusApiEnum.SUCCESS] = statusCache.success;
        countPerStatus[ECpmCTaskStatusApiEnum.FAILED] = statusCache.failed;
        countPerStatus[ECpmCTaskStatusApiEnum.IN_PROGRESS] = statusCache.in_progress;
        const statusDetail = order.map((status) => ({
            color: ServiceRequestUtils.statusTagsByStatus[status].iconStyle!['color'],
            value: statusCache.total ? (countPerStatus[status] / statusCache.total) * 100 : 0
        }));

        let statusTooltip = order
            .filter((status) => countPerStatus[status] > 0)
            .map((status) => `${StringUtils.toHumanFormatedStr(status)}: ${countPerStatus[status]}`)
            .join('\n');

        if (order.filter((status) => countPerStatus[status] > 0).length > 1)
            statusTooltip += '\n\nTotal: ' + statusCache.total;

        return {
            details: statusDetail,
            tooltip: statusTooltip,
            percentage:
                statusCache.total > 0
                    ? Math.round((countPerStatus[ECpmCTaskStatusApiEnum.SUCCESS] / statusCache.total) * 100)
                    : 0
        };
    }

    // ===============================================================
    // ===============================================================

    /**
     * Ensure that the service request has a status cache.
     * @param serviceRequest The service request to check.
     * @returns The status cache of the service request, or a default status cache if none exists.
     */
    private static ensureStatusCache(serviceRequest: CpmCServiceRequestApiModel) {
        return (
            (serviceRequest.task_status_cache as unknown as CpmCServiceRequestTaskStatusCacheApiSchema) ?? {
                total: 1,
                created: 0,
                ready: 0,
                success: 0,
                failed: 0,
                in_progress: 0,
                waiting_user_input: 0
            }
        );
    }
}
